/**
 * Compliance Service - Compliance monitoring API calls
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractList, extractErrorMessage } from '../utils/responseHelpers';

const client = createApiClient(apiConfig.getComplianceServiceUrl());

/**
 * Get compliance analytics summary
 */
export const getComplianceSummary = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/summary', { params: dateRange });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch compliance summary.') };
  }
};

/**
 * Get most common compliance flags
 */
export const getTopFlags = async (dateRange) => {
  try {
    const response = await client.get('/api/v1/analytics/top-flags', { params: dateRange });
    return { success: true, data: extractList(response.data, 'flags') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch top flags.') };
  }
};

/**
 * Get compliance checks list with pagination
 */
export const getComplianceChecks = async (params) => {
  try {
    const response = await client.get('/api/v1/checks', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch checks.') };
  }
};

/**
 * Get compliance rules
 */
export const getComplianceRules = async () => {
  try {
    const response = await client.get('/api/v1/rules');
    return { success: true, data: extractList(response.data, 'rules') };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch rules.') };
  }
};

/**
 * Create compliance rule
 */
export const createComplianceRule = async (ruleData) => {
  try {
    const response = await client.post('/api/v1/rules', ruleData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to create rule.') };
  }
};

/**
 * Update compliance rule
 */
export const updateComplianceRule = async (ruleId, ruleData) => {
  try {
    const response = await client.put(`/api/v1/rules/${ruleId}`, ruleData);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to update rule.') };
  }
};

/**
 * Delete compliance rule
 */
export const deleteComplianceRule = async (ruleId) => {
  try {
    const response = await client.delete(`/api/v1/rules/${ruleId}`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to delete rule.') };
  }
};

const complianceServiceDefault = {
  getComplianceSummary,
  getTopFlags,
  getComplianceChecks,
  getComplianceRules,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
};

export default complianceServiceDefault;
